import { getHubPlanId, getPlanSnapshot } from "./subscriptionState";
import { formatCatalogPlanName, resolveCatalogPlan } from "../services/subscriptionCatalog";

export const PROFILE_RENEW_SOON_DAYS = 7;
export const PROFILE_HEALTHY_DAYS = 30;

const MS_PER_DAY = 1000 * 60 * 60 * 24;

export function isTrialActiveSubscriptionStatus(subscriptionStatus) {
  return String(subscriptionStatus || "").trim().toLowerCase() === "trial_active";
}

export function formatSubscriptionDateTime(dateStr) {
  if (!dateStr) return "—";
  try {
    return new Date(dateStr).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  } catch {
    return dateStr;
  }
}

export function formatSubscriptionDateShort(dateStr) {
  if (!dateStr) return "—";
  try {
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
}

export function computeSubscriptionTimeline(startDate, expiryDate, now = new Date()) {
  if (!expiryDate) {
    return {
      totalDays: 0,
      remainingDays: 0,
      elapsedDays: 0,
      progressRatio: 0,
      progressPercent: 0,
    };
  }

  const expiry = new Date(expiryDate);
  const today = new Date(now);
  const remainingRaw = Math.ceil((expiry - today) / MS_PER_DAY);
  const remainingDays = remainingRaw > 0 ? remainingRaw : 0;

  if (!startDate) {
    return {
      totalDays: remainingDays,
      remainingDays,
      elapsedDays: 0,
      progressRatio: 0,
      progressPercent: 0,
    };
  }

  const start = new Date(startDate);
  const totalDays = Math.max(Math.ceil((expiry - start) / MS_PER_DAY), 1);
  const elapsedDays = Math.min(Math.max(totalDays - remainingDays, 0), totalDays);
  const progressRatio = Math.min(Math.max(elapsedDays / totalDays, 0), 1);

  return {
    totalDays,
    remainingDays,
    elapsedDays,
    progressRatio,
    progressPercent: Math.round(progressRatio * 100),
  };
}

export function resolveProfileExpiryState(remainingDays) {
  if (remainingDays <= 0) return "expired";
  if (remainingDays <= 1) return "tomorrow";
  if (remainingDays <= PROFILE_RENEW_SOON_DAYS) return "renewSoon";
  return "healthy";
}

export function buildHubPlanPresentation(catalog, hubProfile) {
  const planId = getHubPlanId(hubProfile);
  if (!planId || planId === "none") {
    return {
      planId: "",
      planName: null,
      trialActive: false,
      expiryDate: null,
      startDate: null,
    };
  }

  const catalogPlan = resolveCatalogPlan(catalog, planId);
  return {
    planId,
    planName: formatCatalogPlanName(planId, catalogPlan),
    trialActive: isTrialActiveSubscriptionStatus(hubProfile?.SubscriptionStatus),
    expiryDate: getPlanSnapshot(hubProfile).expiry || hubProfile?.ExpiryDate || null,
    startDate: hubProfile?.StartDate ?? null,
  };
}

export function buildProfileSubscriptionSummary(catalog, hubProfile) {
  const base = buildHubPlanPresentation(catalog, hubProfile);
  if (!base.planName || !base.expiryDate) {
    return {
      ...base,
      hasSubscription: false,
      planTitle: null,
      trialBadgeLabel: null,
      trialDays: null,
      totalDays: 0,
      remainingDays: 0,
      elapsedDays: 0,
      progressRatio: 0,
      progressPercent: 0,
      expiryState: "none",
      showAvatarBadge: false,
      avatarBadgeCount: 0,
    };
  }

  const catalogPlan = resolveCatalogPlan(catalog, base.planId);
  const trialDays = Number(catalogPlan?.trial?.days) || null;
  const timeline = computeSubscriptionTimeline(base.startDate, base.expiryDate);
  const expiryState = resolveProfileExpiryState(timeline.remainingDays);
  const showAvatarBadge = timeline.remainingDays <= PROFILE_RENEW_SOON_DAYS;

  return {
    ...base,
    hasSubscription: true,
    catalogPlan,
    trialDays,
    ...timeline,
    expiryState,
    showAvatarBadge,
    avatarBadgeCount: showAvatarBadge ? 1 : 0,
    planTitle: base.trialActive ? `${base.planName} Trial` : base.planName,
    trialBadgeLabel: trialDays ? `${trialDays}-Day Trial` : "Trial",
  };
}
